export interface DeliveryTimelineTimeInput {
  plannedEta?: string | null;
  latestEta?: string | null;
  actualDeliveryTime?: string | null;
  legacyEta?: string | null;
  fallbackTime?: string;
  status?: string | number;
}

export interface DeliveryTimelineTimes {
  plannedTime: string;
  latestTime: string;
  actualTime: string | null;
  positionTime: string;
  effectiveTime: string;
  comparisonLabel: string | null;
  delayMinutes: number | null;
}

const LOCAL_DATE_TIME_PATTERN = /^\d{4}-\d{2}-\d{2}[ T](\d{2}):(\d{2})/;
const EXPLICIT_TIMEZONE_PATTERN = /(?:Z|[+-]\d{2}:?\d{2})$/i;

export function getDeliveryDropRenderId(
  sheetUuid: string,
  orderUuid: string | undefined,
  orderCode: string | undefined,
  dropIndex: number
): string {
  return `${sheetUuid}:${orderUuid || orderCode || "drop"}:${dropIndex}`;
}

export function groupDeliverySheetRows<T extends { uuid: string }>(rows: readonly T[]): T[][] {
  const groups = new Map<string, T[]>();
  for (const row of rows) {
    const group = groups.get(row.uuid);
    if (group) {
      group.push(row);
    } else {
      groups.set(row.uuid, [row]);
    }
  }
  return Array.from(groups.values());
}

export function formatBangkokTimelineTime(value?: string | null): string | null {
  if (!value) return null;

  const normalized = String(value).trim();
  if (!normalized) return null;

  const localMatch = normalized.match(LOCAL_DATE_TIME_PATTERN);
  if (localMatch && !EXPLICIT_TIMEZONE_PATTERN.test(normalized)) {
    return `${localMatch[1]}:${localMatch[2]}`;
  }

  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return null;

  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Bangkok",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(parsed);
  const hour = parts.find((part) => part.type === "hour")?.value;
  const minute = parts.find((part) => part.type === "minute")?.value;
  return hour && minute ? `${hour}:${minute}` : null;
}

function parseTimelineDateTime(value?: string | null): Date | null {
  if (!value) return null;
  const normalized = String(value).trim();
  if (!normalized) return null;

  const date = new Date(
    LOCAL_DATE_TIME_PATTERN.test(normalized) && !EXPLICIT_TIMEZONE_PATTERN.test(normalized)
      // Admin delivery-sheet timestamps without an offset are Bangkok wall
      // times. Use the same instant for delay/sort as the displayed clock.
      ? `${normalized.replace(" ", "T")}+07:00`
      : normalized
  );
  return Number.isNaN(date.getTime()) ? null : date;
}

export function getLatestDeliveryTimelineValue(
  values: Array<string | null | undefined>
): string | null {
  return values.reduce<string | null>((latest, value) => {
    if (!value) return latest;
    if (!latest) return value;

    const latestDate = parseTimelineDateTime(latest);
    const valueDate = parseTimelineDateTime(value);
    if (latestDate && valueDate) {
      return valueDate.getTime() > latestDate.getTime() ? value : latest;
    }
    return value > latest ? value : latest;
  }, null);
}

function getMinutesFromClock(time: string): number | null {
  const [rawHour, rawMinute] = time.split(":");
  const hour = Number(rawHour);
  const minute = Number(rawMinute);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  return hour * 60 + minute;
}

function calculateDelayMinutes(
  plannedValue: string | null | undefined,
  effectiveValue: string | null | undefined,
  plannedTime: string,
  effectiveTime: string
): number | null {
  const plannedDate = parseTimelineDateTime(plannedValue);
  const effectiveDate = parseTimelineDateTime(effectiveValue);
  if (plannedDate && effectiveDate) {
    return Math.round((effectiveDate.getTime() - plannedDate.getTime()) / 60000);
  }

  const plannedMinutes = getMinutesFromClock(plannedTime);
  const effectiveMinutes = getMinutesFromClock(effectiveTime);
  if (plannedMinutes === null || effectiveMinutes === null) return null;
  return effectiveMinutes - plannedMinutes;
}

export function resolveDeliveryTimelineTimes(
  input: DeliveryTimelineTimeInput
): DeliveryTimelineTimes {
  const plannedSource = input.plannedEta || input.legacyEta || null;
  const latestSource = input.latestEta || input.legacyEta || input.plannedEta || null;
  const actualSource = input.actualDeliveryTime || null;

  const plannedTime =
    formatBangkokTimelineTime(plannedSource) || input.fallbackTime || "--:--";
  const latestTime = formatBangkokTimelineTime(latestSource) || plannedTime;
  const actualTime = formatBangkokTimelineTime(actualSource);
  const normalizedStatus = String(input.status ?? "").toLowerCase();
  const isDelivered = normalizedStatus === "delivered" || input.status === 2;
  const useActualTime = !!actualTime && (isDelivered || !!actualSource);
  const effectiveTime = useActualTime ? actualTime : latestTime;
  const effectiveSource = useActualTime ? actualSource : latestSource;

  let comparisonLabel: string | null = null;
  if (useActualTime) {
    comparisonLabel = `จริง ${actualTime}`;
  } else if (latestTime !== plannedTime) {
    comparisonLabel = `คาด ${latestTime}`;
  }

  return {
    plannedTime,
    latestTime,
    actualTime,
    positionTime: plannedTime,
    effectiveTime,
    comparisonLabel,
    delayMinutes: calculateDelayMinutes(
      plannedSource,
      effectiveSource,
      plannedTime,
      effectiveTime
    ),
  };
}
